/**
 * Плитка показателя.
 *
 * Одно текущее значение — это не столбчатая диаграмма из одного столбца,
 * а плитка. Дельта показывается знаком и словом, а не только цветом:
 * «рост» может быть и хорошим, и плохим — направление задаётся отдельно.
 */

import { ChevronRight, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { Link, type To } from "react-router-dom";

import { cn } from "../cn";

interface StatTileProps {
  label: string;
  value: string | number;
  unit?: string;
  /** Изменение к предыдущему периоду в процентах. */
  deltaPercent?: number | null;
  /** С чем сравнивали — «02.08–31.08»; без него — «к прошлому периоду». */
  comparedTo?: string;
  /** Почему изменения нет — показывается вместо него, если дельты нет. */
  noDeltaNote?: string;
  /** Что считать хорошим: рост показателя или его снижение. */
  goodDirection?: "up" | "down";
  hint?: string;
  /** Куда проваливаться по клику; без неё плитка не кликабельна. */
  to?: To;
  /** Подпись перехода — что именно откроется. */
  toLabel?: string;
}

export function StatTile({
  label,
  value,
  unit,
  deltaPercent,
  comparedTo,
  noDeltaNote,
  goodDirection = "up",
  hint,
  to,
  toLabel = "Показать задачи",
}: StatTileProps) {
  const hasDelta = typeof deltaPercent === "number" && Number.isFinite(deltaPercent);
  const rising = hasDelta && deltaPercent > 0;
  const flat = hasDelta && deltaPercent === 0;
  const good = hasDelta && !flat && (rising ? goodDirection === "up" : goodDirection === "down");

  const Icon = !hasDelta || flat ? Minus : rising ? TrendingUp : TrendingDown;

  const shell = "flex flex-col gap-1 rounded-lg border border-line bg-surface px-3.5 py-3";

  const body = (
    <>
      <span className="text-[11px] font-bold tracking-wide text-faint uppercase">{label}</span>

      <span className="flex items-baseline gap-1">
        <span className="text-2xl leading-none font-extrabold text-fg tabular-nums">{value}</span>
        {unit !== undefined && <span className="text-[13px] text-muted">{unit}</span>}
      </span>

      {hasDelta && (
        <span
          className={cn(
            "flex items-center gap-1 text-[11px] font-semibold tabular-nums",
            // Цвет дублируется словом: направление не должно читаться только цветом.
            flat ? "text-muted" : good ? "text-success" : "text-danger",
          )}
        >
          <Icon className="size-3" aria-hidden />
          {flat
            ? "без изменений"
            : `${rising ? "+" : ""}${deltaPercent.toFixed(1)}% к ${comparedTo ?? "прошлому периоду"}`}
        </span>
      )}
      {!hasDelta && noDeltaNote !== undefined && (
        <span className="text-[11px] text-faint">{noDeltaNote}</span>
      )}

      {hint !== undefined && <span className="text-[11px] text-faint">{hint}</span>}

      {to !== undefined && (
        <span className="mt-0.5 flex items-center gap-0.5 text-[11px] font-semibold text-accent">
          {toLabel}
          <ChevronRight className="size-3" aria-hidden />
        </span>
      )}
    </>
  );

  if (to === undefined) return <div className={shell}>{body}</div>;

  // Плитка-ссылка ведёт себя как ссылка: вся площадь — одна цель, фокус
  // виден, а возможность провалиться подписана словом, а не только
  // подсветкой при наведении.
  return (
    <Link
      to={to}
      className={cn(
        shell,
        "transition-colors hover:border-accent hover:bg-sunken",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
      )}
    >
      {body}
    </Link>
  );
}
